import { SetStateAction, useEffect } from "react";
import styled from "styled-components";
import { State } from "../../../types";
import { TimeSlot } from "../../../types/TimeSlot";
import DateHelper from "../../../helpers/DateHelper";
import { CrossSellVenueAndAvailability } from "../../../types/CrossSellVenueAndAvailability";
import { defaultThemeStyles } from "../../../mocks";
import TimeSlotHelper from "../../../helpers/TimeSlotHelper";
import LoadMore from "../../../components/LoadMore/LoadMore";
import { useTranslation } from "react-i18next";
import {
  Divider,
  NoAvailableTimesModal,
  TimeSlotSelector,
  VenueTimeslotSelector,
} from "../../../components";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 13px;
`;

const StyledVenueList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding-left: 15px;
  padding-right: 15px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export type AvailabilityOutsideTwoHoursProps = {
  getMoreAvailableTimes: () => TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  setSelectedTimeSlot: React.Dispatch<React.SetStateAction<TimeSlot | null>>;
  state: State;
  venueSearchLimit: number;
  isLoading: boolean;
  loadMore: () => void;
  setState: (payload: Partial<State>) => void;
};

export const AvailabilityOutsideTwoHours: React.FC<
  AvailabilityOutsideTwoHoursProps
> = ({
  state,
  selectedTimeSlot,
  getMoreAvailableTimes,
  setSelectedTimeSlot,
  venueSearchLimit,
  isLoading,
  loadMore,
  setState,
}) => {
  const { t } = useTranslation();
  const styles = state.crossSellSetup?.ThemeStyles || defaultThemeStyles;

  useEffect(() => {
    if (
      state.availableCrossSellVenues.length === 0 &&
      state.crossSellSetup &&
      state.crossSellSetup.CrossSellVenues.length > 0
    ) {
      setState({
        availableCrossSellVenues: state.crossSellSetup.CrossSellVenues.slice(
          0,
          venueSearchLimit
        ),
      });
    }
  }, [
    setState,
    state.availableCrossSellVenues.length,
    state.crossSellSetup,
    venueSearchLimit,
  ]);

  const handleSelectTime = (
    time: SetStateAction<string | null>,
    va: CrossSellVenueAndAvailability
  ) => {
    const timeSlot = time
      ? TimeSlotHelper.makeTimeSlotAvailabilitySearch(time, va)
      : null;
    setSelectedTimeSlot(timeSlot);
  };

  const getLoadMore = () => {
    if (
      state.crossSellSetup?.CrossSellVenues &&
      state.searchedCrossSellVenues.length <
        state.crossSellSetup?.CrossSellVenues.length
    ) {
      return (
        <LoadMore
          numberToLoad={Math.min(
            venueSearchLimit,
            state.crossSellSetup?.CrossSellVenues.length -
              state.searchedCrossSellVenues.length
          )}
          isLoading={isLoading}
          loadMore={loadMore}
          styles={styles}
        />
      );
    }
  };

  return (
    <StyledContainer data-id="availability-outside-two-hours">
      <NoAvailableTimesModal
        styles={styles}
        availableTimes={DateHelper.findClosestTimeSlotsAfterSelectedTime(
          state.initialTimeSlot,
          getMoreAvailableTimes()
        )}
        selectedTime={selectedTimeSlot}
        setSelectedTimeSlot={(timeSlot) => setSelectedTimeSlot(timeSlot)}
      />
      {state.searchedCrossSellVenues &&
        state.searchedCrossSellVenues.filter(
          (csv) => csv.AvailabilitySearchResponse.TimeSlots.length > 0
        ).length > 0 && (
          <Divider
            styles={styles}
            dataId="availability-at-other-venues-divider"
          >
            {t("AvailabilityAtOurOtherVenues")}
          </Divider>
        )}
      {isLoading && state.searchedCrossSellVenues.length === 0 && (
        <Divider styles={styles} dataId="searching-other-venues-divier">
          {t("SearchingOurOtherNearbyVenues")}
        </Divider>
      )}
      <StyledVenueList data-id="venue-list">
        {state.searchedCrossSellVenues
          .filter((csv) => csv.AvailabilitySearchResponse.TimeSlots.length > 0)
          .sort((a, b) => a.CrossSellVenue.Priority - b.CrossSellVenue.Priority)
          .map((csv) => (
            <VenueTimeslotSelector
              providerName={csv.CrossSellVenue.Name}
              providerLocation={csv.CrossSellVenue.Address}
              providerLocationUrl={`https://www.google.com/maps/search/?api=1&query=${csv.CrossSellVenue.Name},${csv.CrossSellVenue.Address}`}
              key={csv.CrossSellVenue.MicrositeName}
              styles={styles}
            >
              <TimeSlotSelector
                initialTimeSlot={state.initialTimeSlot}
                micrositeName={csv.CrossSellVenue.MicrositeName}
                times={csv.AvailabilitySearchResponse.TimeSlots}
                selectedTime={
                  csv.CrossSellVenue.MicrositeName ===
                  selectedTimeSlot?.MicrositeName
                    ? DateHelper.sliceTimeSlot(selectedTimeSlot!)
                    : null
                }
                setSelectedTime={(time) => handleSelectTime(time, csv)}
                styles={styles}
              />
            </VenueTimeslotSelector>
          ))}
      </StyledVenueList>
      {getLoadMore()}
    </StyledContainer>
  );
};

export default AvailabilityOutsideTwoHours;
